import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const SYNC_INTERVAL_MS = 60_000; // 1 minute

/**
 * Periodically calls check-subscription to sync Stripe → local DB.
 * Fires on login, page load, and every 60s.
 */
export function useSubscriptionSync() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const lastSyncRef = useRef(0);

  useEffect(() => {
    if (!user?.id) return;

    const sync = async () => {
      const now = Date.now();
      if (now - lastSyncRef.current < 10_000) return; // debounce 10s
      lastSyncRef.current = now;

      try {
        const { error } = await supabase.functions.invoke("check-subscription");
        if (!error) {
          queryClient.invalidateQueries({ queryKey: ["subscription", user.id] });
        }
      } catch (e) {
        console.error("[useSubscriptionSync] sync failed:", e);
      }
    };

    // Sync on mount (login / page load)
    sync();

    // Periodic sync
    const interval = setInterval(sync, SYNC_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user?.id, queryClient]);
}
